import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AuthStateService } from './auth-state.service';

/**
 * Current User interface matching /api/me response
 */
export interface CurrentUser {
  id: number;
  username: string;
  email: string;
  firstname: string;
  lastname: string;
  fullname: string;
  avatar: string | null;
  roles: string[];
  permissions: string[];
  totp_enabled: boolean;
}

/**
 * Service to manage current authenticated user
 * Provides access to /api/me endpoint
 */
@Injectable({
  providedIn: 'root',
})
export class CurrentUserService {
  private readonly currentUserSubject = new BehaviorSubject<CurrentUser | null>(
    null,
  );
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly authState: AuthStateService,
  ) {}

  /**
   * Fetch current user from /api/me
   * @returns Observable<CurrentUser>
   */
  getCurrentUser(): Observable<CurrentUser> {
    return this.http.get<CurrentUser>('/me', { withCredentials: true }).pipe(
      tap((user) => {
        this.currentUserSubject.next(user);
        // Store minimal info using AuthStateService
        this.authState.setUserInfo({
          fullname: user.fullname,
          firstname: user.firstname,
          lastname: user.lastname,
          avatar: user.avatar,
        });
      }),
    );
  }

  /**
   * Get cached user data
   */
  getCachedUser(): CurrentUser | null {
    return this.currentUserSubject.value;
  }

  /**
   * Clear user data on logout
   */
  clearUser(): void {
    this.currentUserSubject.next(null);
    this.authState.clearUserInfo();
  }

  /**
   * Check if user has specific permission
   */
  hasPermission(permission: string): boolean {
    const user = this.currentUserSubject.value;
    return user?.permissions.includes(permission) ?? false;
  }

  /**
   * Check if user has specific role
   */
  hasRole(role: string): boolean {
    const user = this.currentUserSubject.value;
    return user?.roles.includes(role) ?? false;
  }
}
